import {
  computePolygonArea,
  isPolygon2D,
} from '@shared/helpers/geometry/geometry.helpers';
import type { Shape } from '@shared/models/design/storey.model';

export type ShapeHit = {
  shapeId: string;
  storeyLevel: number;
};

/**
 * Resolve the clicked shape id for a given storey level.
 * When several hits share the level and {@link shapes} is provided, prefers the smallest polygon (nested cutout over enclosing solid).
 * @param hits {@link ShapeHit} from map query (order is not reliable)
 * @param storeyLevel Storey index (same definition as {@link MapStoreyProperty.Level})
 * @param shapes Optional storey shapes for area tie-break
 */
export const resolveShapeIdForStoreyLevel = (
  hits: ShapeHit[],
  storeyLevel: number,
  shapes?: Shape[],
): string | undefined => {
  const matching = hits.filter((hit) => hit.storeyLevel === storeyLevel);
  if (!matching.length) return undefined;
  if (!shapes?.length) return matching[0]?.shapeId;

  const areaForId = (shapeId: string): number => {
    const shape = shapes.find((s) => s.id === shapeId);
    if (!shape || !isPolygon2D(shape.points)) return Number.POSITIVE_INFINITY;
    return computePolygonArea(shape.points);
  };

  let bestId: string | undefined;
  let bestArea = Number.POSITIVE_INFINITY;
  for (const hit of matching) {
    const a = areaForId(hit.shapeId);
    if (!bestId || a < bestArea) {
      bestId = hit.shapeId;
      bestArea = a;
    }
  }
  return bestId;
};
